import { Loader2, Plus, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  type TreatmentCheckInV2,
  useCreateCheckInV2Mutation,
  useListCheckInsV2Query,
} from '@/services/api/treatmentCheckInV2Api';
import {
  useCreateTreatmentCheckInMutation,
  useGetTreatmentCheckInsQuery,
} from '@/services/api/treatmentService';
import {
  type TreatmentCheckIn,
  TreatmentCheckInStatus,
} from '@/services/types/treatment-types';

import { DraftCheckInConfirmationDialog } from './DraftCheckInConfirmationDialog';
import { FlagIssueForm } from './edit/FlagIssueForm';
import { LastCheckInOverview } from './edit/LastCheckInOverview';
import { TreatmentCheckInCard } from './TreatmentCheckInCard';

// ── Unified check-in item type ───────────────────────────────────────────────

export type UnifiedCheckIn =
  | { version: 'v1'; checkIn: TreatmentCheckIn }
  | { version: 'v2'; checkIn: TreatmentCheckInV2 };

// ── Component ────────────────────────────────────────────────────────────────

interface TreatmentCheckInsPageProps {
  matterId: string;
}

export const TreatmentCheckInsPage = ({
  matterId,
}: TreatmentCheckInsPageProps) => {
  const {
    data: v1Data = [],
    isLoading: isLoadingV1,
    error: errorV1,
  } = useGetTreatmentCheckInsQuery(matterId);
  const [createCheckIn, { isLoading: isCreating }] =
    useCreateTreatmentCheckInMutation();
  const [createCheckInV2, { isLoading: isCreatingV2 }] =
    useCreateCheckInV2Mutation();
  const navigate = useNavigate();
  const [isDraftConfirmationOpen, setIsDraftConfirmationOpen] = useState(false);

  const { data: v2Data = [], isLoading: isLoadingV2 } =
    useListCheckInsV2Query(matterId);

  // Merge V1 + V2 into a single list sorted by created_at desc
  const allCheckIns = useMemo<UnifiedCheckIn[]>(() => {
    const v1Items: UnifiedCheckIn[] = v1Data.map((ci) => ({
      version: 'v1' as const,
      checkIn: ci,
    }));
    const v2Items: UnifiedCheckIn[] = v2Data.map((ci) => ({
      version: 'v2' as const,
      checkIn: ci,
    }));
    return [...v1Items, ...v2Items].sort(
      (a, b) =>
        new Date(b.checkIn.created_at).getTime() -
        new Date(a.checkIn.created_at).getTime(),
    );
  }, [v1Data, v2Data]);

  const isLoading = isLoadingV1 || isLoadingV2;
  const error = errorV1;

  const mostRecentV1CheckIn = v1Data[0];
  const hasDraft =
    mostRecentV1CheckIn?.status === TreatmentCheckInStatus.DRAFT;

  // V1 check-in creation
  const proceedToCreateV1CheckIn = async () => {
    try {
      const newCheckIn = await createCheckIn({ matterId }).unwrap();
      navigate(
        `/matters/${matterId}/treatment/check-ins/${newCheckIn.id}/edit`,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to create check-in', e);
    }
  };

  const handleNewCheckIn = async () => {
    if (hasDraft) {
      setIsDraftConfirmationOpen(true);
    } else {
      await proceedToCreateV1CheckIn();
    }
  };

  const handleEditDraft = () => {
    if (mostRecentV1CheckIn) {
      navigate(
        `/matters/${matterId}/treatment/check-ins/${mostRecentV1CheckIn.id}/edit`,
      );
    }
    setIsDraftConfirmationOpen(false);
  };

  // V2 Smart Check-in creation
  const handleNewSmartCheckIn = async () => {
    try {
      const newCheckIn = await createCheckInV2({ matterId }).unwrap();
      navigate(
        `/matters/${matterId}/treatment/check-in-v2/${newCheckIn.id}`,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to create smart check-in', e);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2">
          <LastCheckInOverview matterId={matterId} />
        </div>

        <div className="lg:col-span-1">
          {mostRecentV1CheckIn && (
            <FlagIssueForm checkInId={mostRecentV1CheckIn.id} />
          )}
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Treatment Check-ins
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleNewCheckIn}
                disabled={isCreating || isCreatingV2}
                className="inline-flex items-center gap-2 bg-black text-white text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-800 disabled:opacity-50"
              >
                {isCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                New Check-in
              </button>
              <button
                type="button"
                onClick={handleNewSmartCheckIn}
                disabled={isCreating || isCreatingV2}
                className="inline-flex items-center gap-2 border border-purple-300 bg-purple-50 text-purple-700 text-sm font-medium px-3 py-2 rounded-md hover:bg-purple-100 disabled:opacity-50"
              >
                {isCreatingV2 ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Smart Check-in
              </button>
            </div>
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}

          {error && (
            <p className="text-sm text-red-500">Failed to load check-ins.</p>
          )}

          {!isLoading && allCheckIns.length === 0 && (
            <p className="text-sm text-gray-500">No check-ins yet.</p>
          )}

          <div className="space-y-3">
            {allCheckIns.map((item) => (
              <TreatmentCheckInCard
                key={item.checkIn.id}
                matterId={matterId}
                item={item}
              />
            ))}
          </div>
        </div>
      </div>
      <DraftCheckInConfirmationDialog
        isOpen={isDraftConfirmationOpen}
        onOpenChange={setIsDraftConfirmationOpen}
        onConfirm={proceedToCreateV1CheckIn}
        onEditDraft={handleEditDraft}
      />
    </>
  );
};
